import React, { useEffect, useRef, useState } from "react";
import { useFormik } from "formik";
import moment from "moment";
import { formatDateSeparator } from "../../../../utils/commonfunction";
import { getMessages, closeSession, getRelatedContext, createOrGetSessionAdmin } from "../../services/supportChat.services";
import { SOCKET_CLIENT } from "../../../../utils/socket";
import { getUser } from "../../../../utils/commonfunction";
import { ROLE, DYNAMIC_VARIABLES } from '../../../../utils/Constants';


export default function SupportChatWindow({ activeSession, onSessionUpdate, onSelectSession }) {
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [connectModalOpen, setConnectModalOpen] = useState(false);
    const [primaryMatch, setPrimaryMatch] = useState(null);
    const [contextLoading, setContextLoading] = useState(false);
    const [recentBookings, setRecentBookings] = useState([]);
    const [searchableList, setSearchableList] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [activeTab, setActiveTab] = useState("bookings");
    const chatWindowRef = useRef();
    const SENDER = getUser(ROLE.SUPER_ADMIN);

    const fetchConnectContext = async (query = "") => {
        if (!activeSession?.id) return null;
        setContextLoading(true);
        try {
            const res = await getRelatedContext({
                session_id: activeSession.id,
                search: query
            });
            if (res?.data) {
                setRecentBookings(res.data.recentBookings || []);
                setSearchableList(res.data.searchableList || []);
                setPrimaryMatch(res.data.primaryMatch || null);
                return res.data;
            }
        } catch (err) {
            console.error("Error fetching related context:", err);
        } finally {
            setContextLoading(false);
        }
        return null;
    };

    const handleConnectClick = async () => {
        setSearchTerm("");
        const data = await fetchConnectContext("");
        const targetType = activeSession?.user ? 'astrologer' : 'user';

        // Direct 1-click connect if order/booking matched a specific target person or recent booking
        if (data?.primaryMatch?.target_person?.id) {
            handleConnectPerson(data.primaryMatch.target_person.id, targetType);
        } else if (data?.recentBookings?.length > 0 && data.recentBookings[0]?.target_person?.id) {
            handleConnectPerson(data.recentBookings[0].target_person.id, targetType);
        } else {
            setConnectModalOpen(true);
        }
    };

    useEffect(() => {
        if (connectModalOpen) {
            fetchConnectContext(searchTerm);
        }
    }, [connectModalOpen, activeSession?.id]);

    const handleConnectPerson = async (targetId, targetType) => {
        try {
            const res = await createOrGetSessionAdmin({
                user_id: targetType === 'user' ? targetId : null,
                astrologer_id: targetType === 'astrologer' ? targetId : null,
                user_type: targetType
            });
            if (res?.data) {
                setConnectModalOpen(false);
                if (onSelectSession) {
                    onSelectSession(res.data);
                } else if (onSessionUpdate) {
                    onSessionUpdate();
                }
            }
        } catch (err) {
            console.error("Error connecting to session:", err);
        }
    };


    const fetchMessages = async () => {
        if (!activeSession?.id) return;
        try {
            const response = await getMessages(activeSession.id);
            // Handle multiple response formats for compatibility
            // The backend returns the array directly in the 'data' field, 
            // and axios interceptor returns the JSON body as 'response'.
            const data = response?.data || response;
            const finalMessages = Array.isArray(data) ? data : (data?.messages || []);

            setMessages(finalMessages);
            setTimeout(scrollToBottom, 100);
        } catch (err) {
            console.error("Error fetching support messages:", err);
        }
    };

    useEffect(() => {
        if (activeSession?.id) {
            fetchMessages();
            const textarea = document.getElementById('support-chat-textarea');
            if (textarea) textarea.style.height = 'auto';

            const joinRoom = () => {
                console.log(`👤 Joining support room: ${activeSession.id}`);
                SOCKET_CLIENT.emit("join_support", { session_id: activeSession.id });
            };

            // 1. Join Socket Room (if connected)
            if (SOCKET_CLIENT.connected) {
                joinRoom();
            } else {
                SOCKET_CLIENT.on("connect", joinRoom);
            }

            // 2. Listen for New Messages
            const handleNewMessage = (message) => {
                console.log("📩 Received support message:", message);
                if (message?.session_id === activeSession.id) {
                    setMessages((prev) => [...prev, message]);
                    if (onSessionUpdate) onSessionUpdate();
                }
            };
            SOCKET_CLIENT.on("receive_support_message", handleNewMessage);

            return () => {
                console.log(`👤 Leaving support room: ${activeSession.id}`);
                SOCKET_CLIENT.off("connect", joinRoom);
                SOCKET_CLIENT.off("receive_support_message", handleNewMessage);
            };
        } else {
            setMessages([]);
        }
    }, [activeSession?.id]);



    const messageForm = useFormik({
        initialValues: {
            message: "",
            session_id: activeSession?.id || "",
        },
        enableReinitialize: true,
        onSubmit: async (values, { resetForm }) => {
            if (!values.message.trim()) return;
            try {
                // We use Socket for sending message
                SOCKET_CLIENT.emit("send_support_message", {
                    session_id: activeSession.id,
                    message: values.message,
                    message_type: 'text'
                });
                resetForm();
                const textarea = document.getElementById('support-chat-textarea');
                if (textarea) textarea.style.height = 'auto';
                // We don't call fetchMessages() here because receive_support_message will update the list
                if (onSessionUpdate) onSessionUpdate();
            } catch (err) {
                console.error("Error sending socket message:", err);
            }
        },

    });

    const handleCloseSession = async () => {
        if (window.confirm("Are you sure you want to close this session?")) {
            try {
                await closeSession(activeSession.id);
                if (onSessionUpdate) onSessionUpdate();
            } catch (err) {
                console.error("Error closing session:", err);
            }
        }
    };

    const scrollToBottom = () => {
        if (chatWindowRef.current) {
            chatWindowRef.current.scrollTo({
                top: chatWindowRef.current.scrollHeight,
                behavior: "smooth",
            });
        }
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleKeyPress = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            if (messageForm.values.message.trim()) {
                messageForm.handleSubmit();
            }
        }
    };

    if (!activeSession) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center h-100 bg-light text-muted">
                <i className="ti ti-message-dots" style={{ fontSize: "3rem", marginBottom: "10px" }}></i>
                <p>Select a session to begin messaging.</p>
            </div>
        );
    }

    const receiverName = activeSession.user?.name || activeSession.astrologer?.name || "Anonymous";

    return (
        <div className="d-flex flex-column h-100 w-100 p-0">
            {/* 1. Chat Header */}
            <div className="p-3 bg-white border-bottom shadow-sm d-flex justify-content-between align-items-center">
                <div className="d-flex gap-3 align-items-center">
                    <figure className="avatar avatar-md m-0">
                        <img
                            src={activeSession.user?.profile_image || activeSession.astrologer?.profile_image || (DYNAMIC_VARIABLES.SERVER_URL ? `${DYNAMIC_VARIABLES.SERVER_URL}/public/uploads/users/default.jpg` : 'http://localhost:5000/public/uploads/users/default.jpg')}
                            alt="profile_image"
                            className="rounded-circle"
                            style={{ width: "40px", height: "40px" }}
                            onError={(e) => {
                                e.target.onerror = null;
                                e.target.src = DYNAMIC_VARIABLES.SERVER_URL ? `${DYNAMIC_VARIABLES.SERVER_URL}/public/uploads/users/default.jpg` : 'http://localhost:5000/public/uploads/users/default.jpg';
                            }}
                        />
                    </figure>
                    <div>
                        <h5 className="mb-0">{receiverName}</h5>
                        <small className="text-muted">
                            {activeSession.user ? "User Support Case" : "Astrologer Support Case"}
                        </small>
                    </div>
                </div>
                <div className="d-flex align-items-center gap-2">
                    <button
                        className="btn btn-sm btn-outline-primary shadow-sm rounded-pill px-3 py-1 me-2 d-flex align-items-center gap-1"
                        onClick={handleConnectClick}
                        style={{ fontSize: "0.82rem", fontWeight: "600" }}
                        title={activeSession.user ? "Connect to corresponding Astrologer for order" : "Connect to corresponding User for order"}
                    >
                        💬 {activeSession.user ? "Connect Astrologer" : "Connect User"}
                    </button>

                    {activeSession.status === 'open' ? (
                        <button className="btn btn-outline-danger btn-sm" onClick={handleCloseSession}>
                            Close Session
                        </button>
                    ) : (
                        <span className="badge bg-secondary">Closed Case</span>
                    )}
                </div>
            </div>

            {/* 2. Messages Area */}
            <div className="flex-grow-1 p-4 overflow-auto bg-light" ref={chatWindowRef}>
                <ul className="list-unstyled p-0 m-0">
                    {(() => {
                        let lastDate = null;
                        return messages?.map((msg, idx) => {
                            const msgDate = moment(msg.createdAt).format("YYYY-MM-DD");
                            const showSeparator = msgDate !== lastDate;
                            lastDate = msgDate;

                            const isMyMsg =
                                msg.sender_type?.toLowerCase() === "admin" ||
                                msg.senderType?.toLowerCase() === "admin" ||
                                (SENDER && (msg.sender_id == SENDER.id || msg.sender_id == SENDER._id));

                            return (
                                <React.Fragment key={idx}>
                                    {showSeparator && (
                                        <li className="d-flex justify-content-center my-4">
                                            <span className="badge bg-secondary text-white px-3 py-2 rounded-pill" style={{ fontSize: "0.8rem", opacity: 0.8 }}>
                                                {formatDateSeparator(msg.createdAt)}
                                            </span>
                                        </li>
                                    )}
                                    <li className={`d-flex ${isMyMsg ? "justify-content-end" : "justify-content-start"} mb-3`}>
                                        <div className={`d-flex flex-column ${isMyMsg ? "align-items-end" : "align-items-start"}`} style={{ maxWidth: "80%" }}>
                                            <div className={`chat-bubble ${isMyMsg ? "bg-primary text-white" : "bg-white border text-dark"} rounded-4 py-2 px-3 shadow-sm`} style={{ width: "fit-content", minWidth: "min-content" }}>
                                                <pre style={{ margin: 0, padding: 0, fontFamily: "inherit", whiteSpace: "pre-wrap", wordBreak: "normal", overflowWrap: "break-word", color: 'inherit' }}>
                                                    {msg.message}
                                                </pre>
                                            </div>
                                            <small className="text-muted mt-1" style={{ fontSize: "0.75rem" }}>
                                                {moment(msg.createdAt).format("hh:mm A")}
                                            </small>
                                        </div>
                                    </li>
                                </React.Fragment>
                            );
                        });
                    })()}
                </ul>
            </div>

            {/* 3. Chat Input */}
            {activeSession.status === 'open' && (
                <form onSubmit={messageForm.handleSubmit}>
                    <div className="p-3 bg-white border-top">
                        <div className="input-group">
                            <textarea
                                id="support-chat-textarea"
                                name="message"
                                className="form-control"
                                placeholder="Type your reply here..."
                                value={messageForm.values.message}
                                onChange={(e) => {
                                    messageForm.handleChange(e);
                                    e.target.style.height = "auto";
                                    e.target.style.height = `${e.target.scrollHeight}px`;
                                }}
                                onKeyDown={handleKeyPress}
                                rows="1"
                                style={{ resize: "none", minHeight: "44px", maxHeight: "120px", overflowY: "auto" }}
                            />
                            <button 
                                type="submit" 
                                className="btn btn-main-primary text-white d-flex align-items-center justify-content-center" 
                                disabled={!messageForm.values.message.trim()}
                                style={{
                                    padding: "0.5rem 1.5rem",
                                    fontWeight: "600",
                                    backgroundColor: !messageForm.values.message.trim() ? "#e9ecef" : "#403fad",
                                    borderColor: !messageForm.values.message.trim() ? "#e9ecef" : "#403fad",
                                    color: !messageForm.values.message.trim() ? "#6c757d" : "#fff",
                                    borderTopLeftRadius: "0",
                                    borderBottomLeftRadius: "0",
                                    transition: "all 0.3s ease",
                                    minHeight: "44px"
                                }}
                            >
                                Reply 🚀
                            </button>
                        </div>
                    </div>
                </form>
            )}

            {/* CONNECT MODAL */}
            {connectModalOpen && (
                <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1050 }} tabIndex="-1">
                    <div className="modal-dialog modal-dialog-centered modal-lg">
                        <div className="modal-content shadow-lg border-0 rounded-3">
                            <div className="modal-header bg-light">
                                <h5 className="modal-title font-weight-bold">
                                    💬 Connect & Chat with {activeSession.user ? "Astrologer" : "User"}
                                </h5>
                                <button
                                    type="button"
                                    className="btn-close"
                                    onClick={() => setConnectModalOpen(false)}
                                ></button>
                            </div>
                            <div className="modal-body p-4">
                                <p className="text-muted small mb-3">
                                    Current session: <strong>{receiverName}</strong> ({activeSession.user ? 'User' : 'Astrologer'}). Select a booking or person below to open a parallel support chat session.
                                </p>

                                {primaryMatch && primaryMatch.target_person && (
                                    <div className="alert alert-primary d-flex justify-content-between align-items-center p-3 mb-3 border-0 rounded-3 shadow-sm" style={{ backgroundColor: '#eef2ff' }}>
                                        <div>
                                            <div className="fw-bold text-primary mb-1">
                                                ⚡ Auto-matched Order #{primaryMatch.order_number || primaryMatch.id} ({primaryMatch.booking_type})
                                            </div>
                                            <div className="small text-dark">
                                                {primaryMatch.item_title} | Connected {activeSession.user ? 'Astrologer' : 'User'}: <strong>{primaryMatch.target_person.name}</strong>
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="btn btn-primary btn-sm px-3 rounded-pill fw-bold"
                                            onClick={() => handleConnectPerson(primaryMatch.target_person.id, activeSession.user ? 'astrologer' : 'user')}
                                        >
                                            💬 Chat Now
                                        </button>
                                    </div>
                                )}

                                {/* Nav Tabs */}
                                <ul className="nav nav-pills mb-3 gap-2">
                                    <li className="nav-item">
                                        <button
                                            type="button"
                                            className={`nav-link btn-sm ${activeTab === 'bookings' ? 'active bg-primary text-white' : 'btn-light text-dark'}`}
                                            onClick={() => setActiveTab('bookings')}
                                        >
                                            📅 Recent Bookings ({recentBookings.length})
                                        </button>
                                    </li>
                                    <li className="nav-item">
                                        <button
                                            type="button"
                                            className={`nav-link btn-sm ${activeTab === 'all' ? 'active bg-primary text-white' : 'btn-light text-dark'}`}
                                            onClick={() => setActiveTab('all')}
                                        >
                                            🔍 Search All {activeSession.user ? 'Astrologers' : 'Users'}
                                        </button>
                                    </li>
                                </ul>

                                {activeTab === 'bookings' && (
                                    <div>
                                        {contextLoading ? (
                                            <div className="p-4 text-center">Loading recent bookings...</div>
                                        ) : recentBookings.length === 0 ? (
                                            <div className="alert alert-warning mb-0">
                                                No recent Puja or Consultation bookings found for this {activeSession.user ? 'User' : 'Astrologer'}. Switch to the "Search All" tab to connect manually.
                                            </div>
                                        ) : (
                                            <div className="list-group overflow-auto" style={{ maxHeight: "320px" }}>
                                                {recentBookings.map((item, idx) => {
                                                    const person = item.target_person;
                                                    const targetType = activeSession.user ? 'astrologer' : 'user';
                                                    return (
                                                        <div key={idx} className="list-group-item d-flex justify-content-between align-items-center p-3 border rounded mb-2">
                                                            <div>
                                                                <div className="d-flex align-items-center gap-2 mb-1">
                                                                    <span className={`badge ${item.booking_type === 'Puja' ? 'bg-purple text-white' : 'bg-info text-white'}`} style={{ backgroundColor: item.booking_type === 'Puja' ? '#7040ad' : '#17a2b8' }}>
                                                                        {item.booking_type}
                                                                    </span>
                                                                    <strong className="text-dark">{item.item_title}</strong>
                                                                    <span className="badge bg-light text-dark border">{item.status}</span>
                                                                </div>
                                                                <div className="small text-muted">
                                                                    <span>Order: #{item.order_number || item.id}</span> | <span>Date: {item.booking_date} {item.booking_time || ''}</span>
                                                                </div>
                                                                <div className="small text-primary mt-1 fw-bold">
                                                                    Connected {targetType === 'astrologer' ? 'Astrologer' : 'User'}: {person?.name} ({person?.email || 'N/A'})
                                                                </div>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="btn btn-primary btn-sm px-3 rounded-pill"
                                                                onClick={() => handleConnectPerson(person.id, targetType)}
                                                            >
                                                                💬 Chat
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}

                                {activeTab === 'all' && (
                                    <div>
                                        <div className="input-group mb-3">
                                            <input
                                                type="text"
                                                className="form-control"
                                                placeholder={`Search ${activeSession.user ? 'astrologer' : 'user'} by name...`}
                                                value={searchTerm}
                                                onChange={(e) => {
                                                    setSearchTerm(e.target.value);
                                                    fetchConnectContext(e.target.value);
                                                }}
                                            />
                                        </div>

                                        {contextLoading ? (
                                            <div className="p-4 text-center">Searching...</div>
                                        ) : searchableList.length === 0 ? (
                                            <div className="p-3 text-center text-muted">No matches found</div>
                                        ) : (
                                            <div className="list-group overflow-auto" style={{ maxHeight: "320px" }}>
                                                {searchableList.map((person) => {
                                                    const targetType = activeSession.user ? 'astrologer' : 'user';
                                                    return (
                                                        <div key={person.id} className="list-group-item d-flex justify-content-between align-items-center p-2 mb-1 border rounded">
                                                            <div className="d-flex align-items-center gap-3">
                                                                <img
                                                                    src={person.profile_image || (DYNAMIC_VARIABLES.SERVER_URL ? `${DYNAMIC_VARIABLES.SERVER_URL}/public/uploads/users/default.jpg` : 'http://localhost:5000/public/uploads/users/default.jpg')}
                                                                    alt={person.name}
                                                                    className="rounded-circle"
                                                                    style={{ width: '36px', height: '36px', objectFit: 'cover' }}
                                                                    onError={(e) => {
                                                                        e.target.onerror = null;
                                                                        e.target.src = DYNAMIC_VARIABLES.SERVER_URL ? `${DYNAMIC_VARIABLES.SERVER_URL}/public/uploads/users/default.jpg` : 'http://localhost:5000/public/uploads/users/default.jpg';
                                                                    }}
                                                                />
                                                                <div>
                                                                    <div className="fw-bold">{person.name}</div>
                                                                    <div className="small text-muted">{person.email || 'N/A'}</div>
                                                                </div>
                                                            </div>
                                                            <button
                                                                type="button"
                                                                className="btn btn-outline-primary btn-sm px-3 rounded-pill"
                                                                onClick={() => handleConnectPerson(person.id, targetType)}
                                                            >
                                                                💬 Chat
                                                            </button>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="modal-footer bg-light p-2">
                                <button
                                    type="button"
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => setConnectModalOpen(false)}
                                >
                                    Close
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
